import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { SectionHeader } from '../../components/UI';
import { DataSourceBadge } from '../../components/sales/DataSourceBadge';
import { AlertTriangle, Wrench, GitCompare, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

// M1 · Sales Integrity — the app's data-hygiene surface.
// This is the ONLY sales page that ships in M1. Every downstream metric on
// Dashboard, Rearview, Console, and Plan is gated by what this page shows:
// tiles will render "See Integrity" instead of a number when the underlying
// input is invalid.

const CLASS_META = {
  value:          { label: 'Value',          icon: AlertTriangle, tint: 'bg-rose-600' },
  structural:     { label: 'Structural',     icon: Wrench,        tint: 'bg-amber-500' },
  reconciliation: { label: 'Reconciliation', icon: GitCompare,    tint: 'bg-indie-cobalt' },
};

const SEV_TINT = {
  high:   'bg-rose-600 text-white',
  medium: 'bg-amber-500 text-indie-ink',
  low:    'bg-indie-ink/20 text-indie-ink',
  info:   'bg-indie-cobalt text-white',
};

function IssueRow({ issue, onSuppress, onUnsuppress }) {
  const [showForm, setShowForm] = React.useState(false);
  const [reason, setReason] = React.useState('');

  return (
    <li
      data-testid={`integrity-issue-${issue.id}`}
      className={`border-t border-indie-ink/10 first:border-t-0 py-3 px-4 flex flex-col gap-1 ${issue.suppressed ? 'opacity-60 bg-indie-cream/60' : ''}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`text-[9px] uppercase tracking-[0.16em] font-semibold px-1.5 py-0.5 ${SEV_TINT[issue.severity] || SEV_TINT.low}`}
        >
          {issue.severity}
        </span>
        <span className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/60">
          {issue.sheet}
        </span>
        <span className="font-mono text-xs text-indie-ink">{issue.location}</span>
        <span className="text-xs text-indie-ink/70">· {issue.metric}</span>
        {issue.suppressed && (
          <span className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 bg-indie-ink/20 text-indie-ink font-semibold" title={`Suppressed by ${issue.suppressed_by}: ${issue.suppression_reason}`}>
            Suppressed
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {issue.suppressed ? (
            <button
              data-testid={`integrity-unsuppress-${issue.id}`}
              onClick={() => onUnsuppress(issue.id)}
              className="text-[10px] uppercase tracking-[0.12em] text-indie-cobalt hover:underline"
            >
              Unsuppress
            </button>
          ) : (
            <button
              data-testid={`integrity-suppress-btn-${issue.id}`}
              onClick={() => setShowForm(!showForm)}
              className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/60 hover:text-indie-ink"
            >
              {showForm ? 'Cancel' : 'Suppress'}
            </button>
          )}
        </div>
      </div>
      <div className="text-sm text-indie-ink leading-snug">{issue.message}</div>
      {issue.suggested_fix && issue.suggested_fix.trim() && (
        <div className="text-xs text-indie-ink/70 italic">
          Suggested fix: {issue.suggested_fix}
        </div>
      )}
      {issue.children && issue.children.length > 0 && (
        <ul className="mt-2 pl-4 border-l-2 border-indie-ink/15 space-y-1">
          {issue.children.map((c) => (
            <li key={c.id} className="text-xs text-indie-ink/70" data-testid={`integrity-child-${c.id}`}>
              <span className="uppercase tracking-[0.12em] text-[9px] mr-2 text-indie-ink/50">{c.class}</span>
              {c.message}
            </li>
          ))}
        </ul>
      )}
      {issue.suppressed && issue.suppression_reason && (
        <div className="text-xs text-indie-ink/60 italic">
          Suppression reason: {issue.suppression_reason}
        </div>
      )}
      {showForm && !issue.suppressed && (
        <form
          data-testid={`integrity-suppress-form-${issue.id}`}
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim().length < 3) return;
            onSuppress(issue.id, reason.trim());
            setShowForm(false);
            setReason('');
          }}
          className="mt-2 flex items-center gap-2"
        >
          <input
            type="text"
            data-testid={`integrity-suppress-reason-${issue.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this known/accepted?"
            className="flex-1 border border-indie-ink/20 px-2 py-1 text-xs"
          />
          <button
            type="submit"
            className="text-[10px] uppercase tracking-[0.12em] font-semibold bg-indie-navy text-indie-cream px-2 py-1 disabled:opacity-50"
            disabled={reason.trim().length < 3}
          >
            Save
          </button>
        </form>
      )}
    </li>
  );
}

export default function SalesIntegrity() {
  const [status, setStatus] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [classFilter, setClassFilter] = useState('all');
  const [sevFilter, setSevFilter] = useState('all');
  const [showSuppressed, setShowSuppressed] = useState(false);
  const [joinFailures, setJoinFailures] = useState([]);
  const [joinExcluded, setJoinExcluded] = useState(0);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get('/sales/integrity/status'),
      api.get('/sales/integrity/issues'),
      api.get('/sales/integrity/join-failures').catch(() => ({ data: { failures: [], internal_excluded_count: 0 } })),
    ])
      .then(([s, i, j]) => {
        setStatus(s.data);
        setIssues(i.data.issues || []);
        setJoinFailures(j.data.failures || []);
        setJoinExcluded(j.data.internal_excluded_count || 0);
      })
      .catch(() => toast.error('Could not load Sales Integrity.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSuppress = async (issueId, reason) => {
    try {
      await api.post(`/sales/integrity/suppressions/${issueId}`, { reason });
      toast.success('Suppressed.');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Suppress failed.');
    }
  };

  const handleUnsuppress = async (issueId) => {
    try {
      await api.delete(`/sales/integrity/suppressions/${issueId}`);
      toast.success('Unsuppressed.');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unsuppress failed.');
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/sales/integrity/sync');
      if (r.data.ok) {
        toast.success(`Sync complete — ${r.data.issue_count} issues detected.`);
        fetchAll();
      } else {
        toast.error(`Sync failed: ${r.data.error || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = useMemo(() => {
    return issues.filter((i) =>
      (classFilter === 'all' || i.class === classFilter) &&
      (sevFilter === 'all' || i.severity === sevFilter) &&
      (showSuppressed ? true : !i.suppressed)
    );
  }, [issues, classFilter, sevFilter, showSuppressed]);

  const countByClass = status?.issue_count_by_class || {};
  const countBySev = status?.issue_count_by_severity || {};
  const lastSynced = status?.last_synced
    ? new Date(status.last_synced).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';

  if (loading) {
    return <div className="label-editorial" data-testid="integrity-loading">Loading Sales Integrity…</div>;
  }

  return (
    <div className="space-y-6" data-testid="sales-integrity-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <SectionHeader
          eyebrow="Sales · Integrity"
          title="Data-integrity console"
          description="Every defect the app can detect in the Sales Snapshot and BHAG sheets, with the exact cell reference the finance/ops owner should fix. Nothing on Dashboard, Rearview, Console, or Plan will derive a metric on top of a defect surfaced here."
        />
        <button
          data-testid="integrity-sync-btn"
          onClick={runSync}
          disabled={syncing}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold bg-indie-navy text-indie-cream px-3 py-2 disabled:opacity-50 hover:bg-indie-ink transition-colors"
        >
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing…' : 'Refresh from sheets'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <DataSourceBadge
          source="Google Sheets"
          asOf={lastSynced}
          confidence={status?.issue_count > 40 ? 'red' : status?.issue_count > 15 ? 'amber' : 'green'}
          testId="integrity-source-badge"
        />
        <span className="text-xs text-indie-ink/60">
          {status?.bhag_rows || 0} BHAG rows · {status?.sales_rows || 0} Sales Snapshot rows scanned
        </span>
      </div>

      {/* Class summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="integrity-class-summary">
        {Object.entries(CLASS_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const count = countByClass[key] || 0;
          const active = classFilter === key;
          return (
            <button
              key={key}
              data-testid={`integrity-class-${key}`}
              onClick={() => setClassFilter(active ? 'all' : key)}
              className={`text-left border-2 p-4 transition-colors ${active
                ? 'border-indie-ink bg-indie-cream'
                : 'border-indie-ink/15 hover:border-indie-ink/40 bg-white'}`}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-6 h-6 ${meta.tint} text-white`}>
                  <Icon size={12} strokeWidth={2} />
                </span>
                <span className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/70 font-semibold">
                  {meta.label}
                </span>
              </div>
              <div className="mt-3 text-3xl font-semibold text-indie-ink">{count}</div>
              <div className="mt-1 text-xs text-indie-ink/70">
                {key === 'value' && 'Impossible values — %>100, sales>qual calls, etc.'}
                {key === 'structural' && 'Formula defects — off-by-one refs, drift, hardcoded overrides.'}
                {key === 'reconciliation' && 'Cross-sheet deltas — BHAG vs Sales Snapshot vs Slack.'}
              </div>
            </button>
          );
        })}
      </div>

      {/* Severity filters */}
      <div className="flex flex-wrap items-center gap-2 border-t border-indie-ink/10 pt-4">
        <span className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
          Severity
        </span>
        {['all', 'high', 'medium', 'low', 'info'].map((s) => (
          <button
            key={s}
            data-testid={`integrity-sev-${s}`}
            onClick={() => setSevFilter(s)}
            className={`text-xs uppercase tracking-[0.12em] px-2 py-1 border transition-colors ${sevFilter === s
              ? 'border-indie-ink bg-indie-ink text-indie-cream'
              : 'border-indie-ink/20 text-indie-ink hover:border-indie-ink/50'}`}
          >
            {s}{s !== 'all' && <span className="ml-1 text-[10px] opacity-70">{countBySev[s] || 0}</span>}
          </button>
        ))}
        {(classFilter !== 'all' || sevFilter !== 'all') && (
          <button
            data-testid="integrity-clear-filters"
            onClick={() => { setClassFilter('all'); setSevFilter('all'); }}
            className="text-xs text-indie-cobalt underline ml-2"
          >
            Clear filters
          </button>
        )}
        <label className="ml-auto flex items-center gap-2 text-xs text-indie-ink/70 cursor-pointer" data-testid="integrity-show-suppressed-toggle">
          <input
            type="checkbox"
            checked={showSuppressed}
            onChange={(e) => setShowSuppressed(e.target.checked)}
            className="cursor-pointer"
          />
          Show suppressed
        </label>
        <span className="text-xs text-indie-ink/60">
          Showing {filtered.length} of {issues.length}
        </span>
      </div>

      {/* Issue list */}
      {filtered.length === 0 ? (
        <div
          data-testid="integrity-empty"
          className="border-2 border-dashed border-indie-ink/15 p-8 text-center text-sm text-indie-ink/60"
        >
          No issues match the current filters.
        </div>
      ) : (
        <ul
          data-testid="integrity-issues-list"
          className="border border-indie-ink/15 bg-white"
        >
          {filtered.map((i) => (
            <IssueRow
              key={i.id}
              issue={i}
              onSuppress={handleSuppress}
              onUnsuppress={handleUnsuppress}
            />
          ))}
        </ul>
      )}

      <div className="text-[11px] text-indie-ink/50 leading-relaxed pt-2 border-t border-indie-ink/10">
        <p>
          M1 · Foundation & Integrity milestone. Validator classes are wired for value-level
          (impossible values), structural (Sheets formulas — off-by-one column refs, definition
          drift, SUM-vs-AVERAGE aggregation mismatch, hardcoded literals in derived chains,
          mixed-basis targets), and cross-sheet reconciliation (BHAG vs Sales Snapshot).
        </p>
        <p className="mt-1">
          Parser-failure counters, refund-reveal audit log, and contact-resolution failures will
          populate as Slack Tier 1 channels and HubSpot integration come online in M3.
        </p>
      </div>

      {/* Contact-join failure panel */}
      <div className="border border-indie-ink/15 bg-white p-5" data-testid="integrity-join-failures">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
            Contact-join failures · Slack events that didn&apos;t resolve to HubSpot
          </div>
          <span className="text-[10px] text-indie-ink/60" data-testid="join-failures-summary">
            Threshold 0.75 · shown: last {joinFailures.length}
            {joinExcluded > 0 && (
              <> · <span className="text-indie-ink/70">{joinExcluded} internal/test, excluded</span></>
            )}
          </span>
        </div>
        {joinFailures.length === 0 ? (
          <div className="mt-3 text-sm text-indie-ink/60 italic">
            No unresolved joins — every recent event matched a HubSpot contact.
          </div>
        ) : (
          <ul className="mt-3 divide-y divide-indie-ink/10 max-h-96 overflow-y-auto">
            {joinFailures.slice(0, 100).map((f, idx) => (
              <li key={`${f.event_email}-${idx}`} className="py-2 flex items-center justify-between text-xs" data-testid={`join-failure-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] px-1.5 py-0.5 bg-indie-ink/10">{f.source_channel}</span>
                  <span className="text-indie-ink">{f.event_name || '(no name)'}</span>
                  {f.event_email && <span className="text-indie-ink/60 italic">{f.event_email}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 font-semibold ${f.reason === 'not_found' ? 'bg-rose-600 text-white' : f.reason === 'no_email' ? 'bg-amber-500 text-indie-ink' : 'bg-indie-ink/20 text-indie-ink'}`}>
                    {f.reason}
                  </span>
                  <span className="font-mono text-indie-ink/60">conf {f.confidence?.toFixed?.(2) ?? '—'}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
